import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CurrencyInput from "@/components/ui/currency-input";
import DateField from "@/components/ui/date-field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { SubmitButton } from "@/components/ui/submit-button";
import VehicleSelect from "@/components/ui/vehicle-select";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { errorHandler } from "@/lib/utils";
import { licensingService } from "@/services/api/licensings";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

const formSchema = z.object({
  vehicle_id: z.number({
    message: "Veículo é obrigatório",
  }),
  due_date: z
    .string({
      message: "Data de vencimento é obrigatória",
    })
    .date("Data de vencimento inválida"),
  ipva: z
    .number()
    .min(0, "O valor do IPVA deve ser maior ou igual a 0")
    .nullable(),
  dpvat: z
    .number()
    .min(0, "O valor do DPVAT deve ser maior ou igual a 0")
    .nullable(),
  expedition: z
    .number()
    .min(0, "O valor da taxa de expedição deve ser maior ou igual a 0")
    .nullable(),
  licensing: z
    .number()
    .min(0, "O valor do licenciamento deve ser maior ou igual a 0")
    .nullable(),
  total: z.number().nullable(),
  payment_date: z.string().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

const LicensingForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vehicle_id: 0,
      due_date: "",
      ipva: null,
      dpvat: null,
      expedition: null,
      licensing: null,
      total: null,
      payment_date: null,
    },
  });

  useEffect(() => {
    const loadLicensing = async () => {
      if (id) {
        try {
          const response = await licensingService.show(Number(id));
          const licensingData = response.data;

          form.reset({
            vehicle_id: licensingData.vehicle_id,
            due_date: licensingData.due_date,
            ipva: licensingData.ipva,
            dpvat: licensingData.dpvat,
            expedition: licensingData.expedition,
            licensing: licensingData.licensing,
            total: licensingData.total,
            payment_date: licensingData.payment_date,
          });
        } catch (err) {
          errorHandler(err, "Erro ao carregar licenciamento");
          navigate("/licensings");
        }
      }
    };

    loadLicensing();
  }, [id, form.reset, navigate, form]);

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: licensingService.store,
    updateService: licensingService.update,
    onSuccess: () => navigate("/licensings"),
  });

  const onSubmit = async (values: FormValues) => {
    const processedValues = {
      ...values,
      ipva: values.ipva ?? undefined,
      dpvat: values.dpvat ?? undefined,
      expedition: values.expedition ?? undefined,
      licensing: values.licensing ?? undefined,
      total: values.total ?? undefined,
      payment_date: values.payment_date ?? undefined,
    };
    handleSubmit(processedValues);
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>
          {id ? "Editar Licenciamento" : "Novo Licenciamento"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Vehicle Selection */}
            <FormField
              control={form.control}
              name="vehicle_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Veículo</FormLabel>
                  <VehicleSelect
                    value={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ipva"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>IPVA (R$)</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value ?? 0}
                      onChange={(value) => field.onChange(value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dpvat"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Seguro DPVAT (R$)</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value ?? 0}
                      onChange={(value) => field.onChange(value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="expedition"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Taxa de expedição (R$)</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value ?? 0}
                      onChange={(value) => field.onChange(value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="licensing"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Taxa de licenciamento (R$)</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value ?? 0}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DateField
              form={form}
              label="Data de vencimento"
              name="due_date"
              className="flex flex-col"
              placeholder="Selecione uma data"
            />

            <div className="flex justify-end space-x-2 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/licensings")}
              >
                Cancelar
              </Button>
              <SubmitButton loading={loading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default LicensingForm;
